import React, { useState, useEffect } from 'react';

// Define the component that represents the modal overlay and content
const TaskRejectModal = ({ isOpen, onClose, taskId, onSubmit }) => {
    // 1. Initialize form state. Status is fixed to REJECTED for this specific modal.
    const [formData, setFormData] = useState({
        taskId: taskId,
        status: 'REJECTED',
        remarks: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionError, setSubmissionError] = useState(null);

    // Update taskId when it changes (e.g., if the parent component re-uses the modal)
    useEffect(() => {
        setFormData(prev => ({ ...prev, taskId: taskId || null }));
    }, [taskId]);

    // Handles input changes for the remarks
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    // Handles the form submission and mock API call
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isSubmitting) return;

        // Basic validation
        if (!formData.remarks.trim()) {
            setSubmissionError("Remarks are required for task rejection.");
            return;
        }

        setIsSubmitting(true);
        setSubmissionError(null);

        // --- API CALL LOGIC ---
        console.log("Submitting rejection payload:", formData);
        try {
            // 🛑 NOTE: Replace the following Promise with your actual API call 
            // (e.g., axios.post('/api/reject-task', formData))
            await new Promise(resolve => setTimeout(resolve, 1500)); 
            
            // Simulate success
            console.log("Task rejection successful.");
            
            // Call the parent's onSubmit handler with the final data
            if (onSubmit) {
                onSubmit(formData);
            }
            
            // Reset state and close modal on success
            setFormData({ taskId: taskId, status: 'REJECTED', remarks: '' });
            onClose();

        } catch (error) {
            console.error("API Submission failed:", error);
            setSubmissionError("Failed to reject task. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Clear state on close
    const handleClose = () => {
        setFormData({ taskId: taskId, status: 'REJECTED', remarks: '' });
        setSubmissionError(null);
        setIsSubmitting(false);
        onClose();
    };


    if (!isOpen) return null;

    return (
        // Modal Backdrop
        <div className="fixed inset-0 z-50 bg-gray-900 bg-opacity-70 flex items-center justify-center p-4" onClick={handleClose}>
            
            {/* Modal Content */}
            <div 
                className="bg-white rounded-xl shadow-2xl w-full max-w-lg transform transition-all duration-300 scale-100 opacity-100" 
                onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal
            >
                <form onSubmit={handleSubmit}>
                    
                    {/* Header - Green Theme */}
                    <div className="flex justify-between items-center p-5 rounded-t-xl bg-green-700 text-white">
                        <h3 className="text-xl text-white font-semibold">
                            Reject Task {taskId ? `(ID: ${taskId})` : ''}
                        </h3>
                        <button 
                            type="button" 
                            onClick={handleClose} 
                            className="text-white hover:text-green-200 transition-colors"
                            aria-label="Close"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                        </button>
                    </div>

                    {/* Body - Form Inputs */}
                    <div className="p-6 space-y-4">
                        
                        {/* Status Field (Read-only as it's implied by the action) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                            <select
                                name="status"
                                value={formData.status}
                                readOnly
                                disabled
                                className="w-full border border-green-300 p-2 rounded-lg bg-green-50 text-green-700 cursor-not-allowed font-semibold"
                            >
                                <option value="REJECTED">REJECTED</option>
                            </select>
                        </div>

                        {/* Remarks Field */}
                        <div>
                            <label htmlFor="remarks" className="block text-sm font-medium text-gray-700 mb-1">
                                Remarks (Required)
                            </label>
                            <textarea
                                id="remarks"
                                name="remarks"
                                value={formData.remarks}
                                onChange={handleChange}
                                rows="4"
                                required
                                placeholder="Explain the reason for rejecting the task..."
                                className="w-full border border-gray-300 p-3 rounded-lg focus:ring-green-500 focus:border-green-500 transition duration-150 resize-none"
                                disabled={isSubmitting}
                            />
                        </div>

                        {/* Error Message */}
                        {submissionError && (
                            <div className="text-sm text-red-600 font-medium bg-red-50 p-3 rounded-lg border border-red-200">
                                {submissionError}
                            </div>
                        )}
                    </div>

                    {/* Footer - Actions */}
                    <div className="p-5 bg-gray-50 flex justify-end space-x-3 rounded-b-xl border-t border-gray-200">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition duration-150"
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:ring-4 focus:ring-green-500 focus:ring-opacity-50 transition duration-150 flex items-center"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : 'Confirm Rejection'}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
};

// Export the modal component directly for use in other files.
export default TaskRejectModal;
